import { useCallback, useEffect, useRef, useState } from "react";

const isEqual = (a: any, b: any) => JSON.stringify(a) === JSON.stringify(b);

type ExtractArrayTypeFromResponse<Response, Key extends keyof Response> =
    Response[Key] extends Array<infer Item> ? Item : never;

export const useFetchAllPaginatedData = <
    FetchFunction extends (payload: any) => Promise<any>,
    Response = Awaited<ReturnType<FetchFunction>>,
    Keys extends keyof Response = keyof Response,
>(
    fetchFunction: FetchFunction,
    basePayload: Omit<Parameters<FetchFunction>[0], "page">,
    resultKey: Keys,
    options?: { enabled?: boolean; batchSize?: number }
) => {
    const [allData, setAllData] = useState<ExtractArrayTypeFromResponse<Response, Keys>[]>([]);
    const [isLoading, setIsLoading] = useState(false); // first load / payload change
    const [isRefetching, setIsRefetching] = useState(false); // manual refetch without clearing data
    const [error, setError] = useState<string | null>(null);

    const lastPayloadRef = useRef<any>(null);
    const fetchFunctionRef = useRef(fetchFunction);
    useEffect(() => {
        fetchFunctionRef.current = fetchFunction;
    }, [fetchFunction]);
    const isFetchingRef = useRef(false); // prevents overlapping fetches
    const isErrorRef = useRef(false); // prevents refetch loops after error
    const pendingPayloadRef = useRef<typeof basePayload | null>(null); // queued payload when fetch is skipped
    const basePayloadRef = useRef(basePayload); // stable ref for payload
    basePayloadRef.current = basePayload; // update on each render
    const payloadKey = JSON.stringify(basePayload); // for dependency tracking

    const [shouldRefetch, setShouldRefetch] = useState(false);
    const refetchResolversRef = useRef<(() => void)[]>([]); // track promises for awaitable refetch()

    const batchSize = options?.batchSize ?? 50;

    const fetchAll = useCallback(
        async (force = false, keepPrevious = false) => {
            try {
                isFetchingRef.current = true;
                if (force) isErrorRef.current = false;

                const currentPayload = basePayloadRef.current;
                const currentFetchFunction = fetchFunctionRef.current;

                // Decide if this is an initial load or payload change
                const payloadChanged = !isEqual(currentPayload, lastPayloadRef.current);
                const isInitialOrPayloadChange = payloadChanged && !keepPrevious;

                setError(null);
                setIsLoading(isInitialOrPayloadChange);
                setIsRefetching(!isInitialOrPayloadChange);

                // Clear previous data only on initial load / payload change
                if (isInitialOrPayloadChange) {
                    setAllData([]);
                }

                const localPayload = currentPayload;

                // Fetch first page
                const firstPage = await currentFetchFunction({
                    ...localPayload,
                    page: { number: 1, size: batchSize },
                });

                const totalPages = firstPage.pagination?.totalPages ?? 1;
                const finalKey = resultKey as keyof typeof firstPage;

                let allResults = [{ page: 1, data: (firstPage[finalKey] as any[]) ?? [] }];

                // Fetch additional pages in parallel if needed
                if (totalPages > 1) {
                    const requests: Promise<{ page: number; data: any[] }>[] = [];
                    for (let page = 2; page <= totalPages; page++) {
                        requests.push(
                            currentFetchFunction({
                                ...localPayload,
                                page: { number: page, size: batchSize },
                            }).then((data) => ({
                                page,
                                data: (data[finalKey] as any[]) ?? [],
                            }))
                        );
                    }
                    const results = await Promise.all(requests);
                    allResults = [...allResults, ...results].sort((a, b) => a.page - b.page);
                }

                // Flatten into one array
                const finalData = allResults.flatMap((p) => p.data);
                setAllData(finalData);
                lastPayloadRef.current = localPayload;
            } catch (err) {
                setError(err instanceof Error ? err.message : String(err));
                isErrorRef.current = true;
            } finally {
                setIsLoading(false);
                setIsRefetching(false);
                isFetchingRef.current = false;
                setShouldRefetch(false);

                // Resolve all awaiting refetch() calls
                if (refetchResolversRef.current.length) {
                    refetchResolversRef.current.forEach((resolve) => resolve());
                    refetchResolversRef.current = [];
                }

                // Check if there's a pending payload that was queued while we were fetching
                if (pendingPayloadRef.current !== null) {
                    const pending = pendingPayloadRef.current;
                    pendingPayloadRef.current = null;
                    // Only fetch if the pending payload is different from what we just fetched
                    if (!isEqual(pending, lastPayloadRef.current)) {
                        // Trigger a re-render to process the pending payload
                        setShouldRefetch(true);
                    }
                }
            }
        },
        [resultKey, batchSize]
    );

    useEffect(() => {
        const enabled = options?.enabled ?? true;
        const currentPayload = basePayloadRef.current;

        // Clear data if disabled
        if (!enabled) {
            setAllData((currentData) => (currentData.length > 0 ? [] : currentData));
            setError(null);
            setIsLoading(false);
            setIsRefetching(false);
            pendingPayloadRef.current = null;
            return;
        }

        const payloadChanged = !isEqual(currentPayload, lastPayloadRef.current);

        // Do nothing if payload hasn't changed and there's no manual refetch
        if (!payloadChanged && !shouldRefetch) return;

        // If already fetching, queue the new payload to fetch after current completes
        if (isFetchingRef.current && !shouldRefetch) {
            pendingPayloadRef.current = currentPayload;
            return;
        }

        // Skip if in error state (unless it's a forced refetch)
        if (isErrorRef.current && !shouldRefetch) return;

        // If it's a manual refetch, keep previous data (keepPrevious = true)
        fetchAll(shouldRefetch, /* keepPrevious */ shouldRefetch);
    }, [fetchAll, shouldRefetch, options?.enabled, payloadKey]);

    // Manual refetch that returns a promise you can await
    const refetch = useCallback((): Promise<void> => {
        return new Promise<void>((resolve) => {
            refetchResolversRef.current.push(resolve);
            setShouldRefetch(true);
        });
    }, []);

    return {
        data: allData,
        isLoading,
        isRefetching,
        error,
        refetch,
    };
};
